/**
 * Lightweight date helpers. Uses native Date parsing where possible;
 * moment-compatible API surface for migrated call sites.
 */
export type DateInput = string | number | Date;

const parseDate = (input?: DateInput): Date => {
  if (input instanceof Date) return input;
  if (input === undefined) return new Date();
  return new Date(input);
};

export const formatDate = (input: DateInput, pattern: string): string => {
  const date = parseDate(input);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tokens: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    MM: pad(date.getMonth() + 1),
    DD: pad(date.getDate()),
    HH: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
  };
  return pattern.replace(/YYYY|MM|DD|HH|mm|ss/g, (token) => tokens[token] ?? token);
};

export const startOfMonth = (input?: DateInput): Date => {
  const date = parseDate(input);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const endOfMonth = (input?: DateInput): Date => {
  const date = parseDate(input);
  date.setMonth(date.getMonth() + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

export const subtractMonths = (input: DateInput, months: number): Date => {
  const date = parseDate(input);
  date.setMonth(date.getMonth() - months);
  return date;
};

export const formatDisplayDate = (input: DateInput, pattern: string): string => {
  const date = parseDate(input);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const tokens: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    DD: String(date.getDate()).padStart(2, '0'),
    MMM: monthNames[date.getMonth()],
  };
  return pattern.replace(/YYYY|MMM|MM|DD/g, (token) => tokens[token] ?? token);
};

export const startOfDay = (input?: DateInput): Date => {
  const date = parseDate(input);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const endOfDay = (input?: DateInput): Date => {
  const date = parseDate(input);
  date.setHours(23, 59, 59, 999);
  return date;
};

export const subtractDays = (input: DateInput, days: number): Date => {
  const date = parseDate(input);
  date.setDate(date.getDate() - days);
  return date;
};

export const isBefore = (a: DateInput, b: DateInput): boolean => parseDate(a).getTime() < parseDate(b).getTime();

export const isAfter = (a: DateInput, b: DateInput): boolean => parseDate(a).getTime() > parseDate(b).getTime();

export const isSameDay = (a: DateInput, b: DateInput): boolean => {
  const d1 = parseDate(a);
  const d2 = parseDate(b);
  return d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate();
};

export const toISOString = (input?: DateInput): string => parseDate(input).toISOString();

export default {
  format: formatDate,
  startOf: (unit: 'day', input?: DateInput) => (unit === 'day' ? startOfDay(input) : parseDate(input)),
  endOf: (unit: 'day', input?: DateInput) => (unit === 'day' ? endOfDay(input) : parseDate(input)),
  subtract: (days: number, unit: 'days', input?: DateInput) => (unit === 'days' ? subtractDays(input ?? new Date(), days) : parseDate(input)),
  isBefore,
  isAfter,
  isSame: (a: DateInput, b: DateInput, unit: 'day') => (unit === 'day' ? isSameDay(a, b) : false),
};
